import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CEREMONY } from "@/constants/testIds";
import { useNarrator } from "@/lib/narrator";

export default function WelcomeCeremony() {
  const navigate = useNavigate();

  // Kick off deck generation in the background as soon as the seeker enters
  useEffect(() => {
    api.post("/deck/generate").catch(() => {});
  }, []);

  const lines = [
    "The room is dim.",
    "The threshold is drawn.",
    "You are already known here.",
    "Nothing you write is graded. Nothing you draw is random.",
    "Breathe once — slowly.",
    "The veil parts on your name.",
  ];

  // Narrate the whole ceremonial passage once, in rhythm with the motion stagger
  const { speak, stop, muted, supported } = useNarrator();
  useEffect(() => {
    if (!supported || muted) return;
    const transcript = lines.join("  ");
    const t = setTimeout(() => speak(transcript, "welcome-ceremony"), 900);
    return () => {
      clearTimeout(t);
      stop();
    };
  }, [muted, supported]);

  return (
    <div
      data-testid={CEREMONY.welcomeContainer}
      className="relative min-h-screen bg-[#080808] overflow-hidden flex items-center justify-center"
    >
      <div className="starfield-subtle" />
      {/* Slow orbital ring */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.2, scale: 1 }}
        transition={{ duration: 3, ease: "easeInOut" }}
        className="absolute w-[820px] h-[820px] rounded-full border border-[#C8A96E]/15 breathe"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.35, scale: 1 }}
        transition={{ duration: 4, ease: "easeInOut", delay: 0.4 }}
        className="absolute w-[520px] h-[520px] rounded-full border border-[#C8A96E]/25 breathe"
      />

      <div className="relative z-10 text-center max-w-2xl px-8">
        <div className="font-overline mb-14 text-[#C8A96E]">Welcome, seeker</div>

        <div className="space-y-6">
          {lines.map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.4, delay: 0.6 + i * 1.2, ease: "easeInOut" }}
              className="font-heading text-2xl sm:text-3xl text-[#F4EFE6] font-light leading-relaxed"
            >
              {line}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.6, delay: 0.6 + lines.length * 1.2, ease: "easeInOut" }}
          className="mt-20"
        >
          <button
            data-testid={CEREMONY.welcomeContinueBtn}
            className="btn-sacred"
            onClick={() => navigate("/ceremony/reveal")}
          >
            Reveal my character
          </button>
        </motion.div>
      </div>
    </div>
  );
}
